<template>
  <IonApp class="app-shell">
    <a class="skip-link" href="#app-content">Skip to content</a>
    <AppLiveRegion />
    <IonMenu
      menu-id="main-menu"
      content-id="app-content"
      type="overlay"
      side="start"
      :swipe-gesture="showMenu"
      :max-edge-start="20"
      @ion-did-open="onMenuOpened"
      @ion-did-close="onMenuClosed"
    >
      <IonHeader class="ion-no-border drawer-header">
        <IonToolbar>
          <div class="drawer-brand">
            <span class="drawer-mark" aria-hidden="true">EP</span>
            <div>
              <IonTitle>Event People</IonTitle>
              <p>Explore and connect</p>
            </div>
          </div>
          <IonButtons class="drawer-close">
            <button
              type="button"
              class="drawer-close-button"
              aria-label="Close navigation"
              @pointerdown.stop.prevent="closeMenu"
              @keydown.enter.prevent="closeMenu"
              @keydown.space.prevent="closeMenu"
            >
              <IonIcon :icon="closeOutline" aria-hidden="true" />
              <span class="sr-only">Close navigation</span>
            </button>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <nav class="menu-nav" aria-label="Main navigation">
          <IonMenuToggle v-for="item in menuItems" :key="item.path" :auto-hide="true">
            <IonItem
              :router-link="item.path"
              :class="{ active: route.path === item.path }"
              lines="none"
              button
              :aria-current="route.path === item.path ? 'page' : undefined"
            >
              <IonIcon class="menu-item-icon" :icon="item.icon" aria-hidden="true" />
              <IonLabel>{{ item.label }}</IonLabel>
            </IonItem>
          </IonMenuToggle>
        </nav>
        <p class="gesture-hint">
          <IonIcon :icon="swapHorizontalOutline" aria-hidden="true" />
          Swipe toward the left edge to close
        </p>
        <div class="legal-links">
          <IonButton
            fill="clear"
            size="small"
            :aria-label="settings.darkMode ? 'Switch to light mode' : 'Switch to dark mode'"
            @click="settings.toggleDarkMode()"
          >
            <IonIcon :icon="settings.darkMode ? sunnyOutline : moonOutline" aria-hidden="true" />
            {{ settings.darkMode ? 'Light' : 'Dark' }}
          </IonButton>
          <IonMenuToggle
            ><IonButton fill="clear" router-link="/privacy">Privacy</IonButton></IonMenuToggle
          >
          <IonMenuToggle
            ><IonButton fill="clear" router-link="/terms">Terms</IonButton></IonMenuToggle
          >
        </div>
      </IonContent>
    </IonMenu>
    <main id="app-content">
      <AppErrorBoundary><IonRouterOutlet /></AppErrorBoundary>
    </main>
  </IonApp>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  IonApp,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonMenu,
  IonMenuToggle,
  IonRouterOutlet,
  IonTitle,
  IonToolbar,
  menuController,
} from '@ionic/vue';
import {
  calendarOutline,
  heartOutline,
  peopleOutline,
  personCircleOutline,
  qrCodeOutline,
  moonOutline,
  sunnyOutline,
  closeOutline,
  swapHorizontalOutline,
} from 'ionicons/icons';
import { useAuthStore } from '@/store/auth.store';
import { useSettingsStore } from '@/store/settings.store';
import AppErrorBoundary from '@/components/AppErrorBoundary.vue';
import AppLiveRegion from '@/components/AppLiveRegion.vue';
import { announce } from '@/utils/announce.util';
import { ROUTE_NAMES } from '@/constants/routes.const';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const settings = useSettingsStore();
watchEffect(() => {
  document.body.classList.toggle('dark', settings.darkMode);
  document.body.classList.toggle('light', !settings.darkMode);
});
const showMenu = computed(
  () => !['/login', '/register', '/privacy', '/terms'].includes(route.path),
);
const menuItems = computed(() => [
  { label: 'Events', path: '/events', icon: calendarOutline },
  { label: 'Directory', path: '/people', icon: peopleOutline },
  ...(auth.isAuthenticated
    ? [
        { label: 'Networking', path: '/networking', icon: qrCodeOutline },
        { label: 'Favorites', path: '/favorites', icon: heartOutline },
        { label: 'Profile', path: '/profile', icon: personCircleOutline },
      ]
    : [
        {
          label: auth.isGuest ? 'Log in or register' : 'Sign in',
          path: '/login',
          icon: personCircleOutline,
        },
      ]),
]);

watch(
  () => route.path,
  () => {
    const title = typeof route.meta.title === 'string' ? route.meta.title : 'Page';
    announce(`${title} page loaded`);
  },
);

let lastActivityCheck = 0;
async function handleActivity() {
  const now = Date.now();
  if (now - lastActivityCheck < 15_000) return;
  lastActivityCheck = now;
  if (await auth.validateInactivity()) {
    await auth.recordActivity();
  } else if (route.meta.requiresAuth && route.path !== '/login') {
    await router.replace({ name: ROUTE_NAMES.LOGIN, query: { expired: '1' } });
  }
}

onMounted(() => {
  window.addEventListener('pointerdown', handleActivity, { passive: true });
  window.addEventListener('keydown', handleActivity);
  document.addEventListener('visibilitychange', handleActivity);
});

onBeforeUnmount(() => {
  window.removeEventListener('pointerdown', handleActivity);
  window.removeEventListener('keydown', handleActivity);
  document.removeEventListener('visibilitychange', handleActivity);
});

function onMenuOpened() {
  announce('Main navigation opened. Select a destination or swipe left to close.');
}

function onMenuClosed() {
  announce('Main navigation closed.');
}

function closeMenu() {
  window.setTimeout(() => void menuController.close('main-menu'), 80);
}
</script>

<style scoped>
#app-content {
  position: relative;
  width: 100%;
  min-height: 100%;
  margin-inline: auto;
  background: var(--app-canvas);
}
.app-shell {
  background: var(--app-canvas);
}
.skip-link {
  display: flex;
  min-height: 44px;
  align-items: center;
  position: fixed;
  z-index: 100;
  top: 0.75rem;
  left: 50%;
  padding: 0.65rem 0.9rem;
  border-radius: var(--app-radius-sm);
  background: var(--app-ink);
  color: var(--app-canvas);
  transform: translate(-50%, -180%);
  transition: transform 180ms var(--app-ease);
}
.skip-link:focus {
  transform: translate(-50%, 0);
}
.menu-nav {
  margin: var(--app-space-3);
  padding: var(--app-space-2);
  border: 1px solid color-mix(in srgb, var(--app-border) 65%, transparent);
  border-radius: var(--app-radius-lg);
  background: var(--app-surface);
  box-shadow: var(--app-shadow-sm);
}
.menu-nav ion-item {
  --background-hover: var(--app-surface-subtle);
  --border-radius: var(--app-radius-sm);
  --min-height: 3.5rem;
  margin-bottom: 0;
  font-size: 0.875rem;
  font-weight: 600;
  transition:
    transform 180ms var(--app-ease),
    opacity 180ms var(--app-ease);
}
.menu-nav ion-item:hover {
  transform: translateX(0.2rem);
}
.menu-nav ion-item.active {
  --background: var(--app-accent-soft);
  --color: var(--ion-color-primary);
}
.legal-links {
  position: absolute;
  bottom: max(1rem, env(safe-area-inset-bottom));
  left: var(--app-space-3);
  display: flex;
}

.drawer-header ion-toolbar {
  --background: var(--app-surface-subtle);
  --min-height: 5.5rem;
  --padding-start: var(--app-space-4);
  --padding-end: var(--app-space-2);
}

:deep(.menu-inner) {
  background: var(--app-surface-subtle);
}

.drawer-close {
  position: absolute;
  z-index: 2;
  top: 50%;
  right: var(--app-space-2);
  transform: translateY(-50%);
}

.drawer-close-button {
  display: grid;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: 0;
  place-items: center;
  border-radius: var(--app-radius-md);
  background: transparent;
  color: var(--app-ink);
  font-size: 1.25rem;
}

.drawer-close-button ion-icon {
  pointer-events: none;
}

.menu-item-icon {
  margin-inline-end: var(--app-space-4);
  color: var(--app-muted);
  font-size: 1.25rem;
}

.menu-nav ion-item.active .menu-item-icon {
  color: var(--app-accent-ink);
}

.drawer-brand {
  display: flex;
  align-items: center;
  gap: var(--app-space-3);
}

.drawer-brand ion-title {
  position: static;
  width: auto;
  height: auto;
  padding: 0;
  text-align: left;
}

.drawer-brand p {
  margin: var(--app-space-1) 0 0;
  color: var(--app-muted);
  font-size: 0.75rem;
}

.drawer-mark {
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border);
  background: var(--app-surface);
  color: var(--app-accent-ink);
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.gesture-hint {
  display: flex;
  align-items: center;
  gap: var(--app-space-2);
  margin: var(--app-space-4) var(--app-space-5);
  color: var(--app-muted);
  font-size: 0.75rem;
}

.gesture-hint ion-icon {
  font-size: 1rem;
}

@media (min-width: 768px) {
  #app-content {
    max-width: 56rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .menu-nav ion-item {
    transition: none;
  }
}
</style>
